import React, { useState, useRef, useEffect } from "react";

const BRAND = { primary: "#00496D", blue: "#0083D3" };

/**
 * Inline OTP verification panel.
 *
 * Drop this inside any form when `otp.step === "awaiting" || "verifying"`.
 *
 * Props:
 *   otp     — return value of useOtpVerification()
 *   email   — the email address the code was sent to
 *   name    — user's name (for resend call)
 *   onBack  — optional callback to go back to editing the email field
 */
export default function OtpVerifyPanel({ otp, email, name, onBack }) {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const refs = useRef([]);
  const isVerifying = otp.step === "verifying";

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  function handleChange(value, idx) {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[idx] = value;
    setDigits(next);
    otp.setError("");
    if (value && idx < 5) refs.current[idx + 1]?.focus();
  }

  function handleKeyDown(e, idx) {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    }
  }

  function handlePaste(e) {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length === 6) {
      setDigits(pasted.split(""));
      refs.current[5]?.focus();
    }
    e.preventDefault();
  }

  async function handleVerify(e) {
    e.preventDefault();
    const code = digits.join("");
    if (code.length < 6) {
      otp.setError("Please enter all 6 digits.");
      return;
    }
    await otp.verify(code);
  }

  const isFilled = digits.join("").length === 6;

  return (
    <div style={{ marginTop: 4 }}>
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <div style={{ width: 48, height: 48, borderRadius: "50%", background: "rgba(0,131,211,0.1)", border: "1.5px solid rgba(0,131,211,0.25)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
          <i className="material-symbols-outlined" style={{ fontSize: 24, color: BRAND.blue }}>mark_email_unread</i>
        </div>
        <p style={{ fontSize: 14, fontWeight: 700, color: BRAND.primary, margin: "0 0 4px" }}>Check your inbox</p>
        <p style={{ fontSize: 13, color: "#6b7280", margin: 0, lineHeight: 1.5 }}>
          We sent a 6-digit code to <strong style={{ color: BRAND.primary }}>{email}</strong>
        </p>
      </div>

      {/* OTP boxes */}
      <form onSubmit={handleVerify} noValidate>
        <div
          onPaste={handlePaste}
          style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 16 }}
        >
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => { refs.current[i] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => handleChange(e.target.value, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              disabled={isVerifying}
              style={{
                width: 44, height: 52,
                textAlign: "center",
                fontSize: 22, fontWeight: 800,
                color: BRAND.primary,
                border: `2px solid ${otp.error ? "#ef4444" : d ? BRAND.blue : "#e5e7eb"}`,
                borderRadius: 10,
                outline: "none",
                boxShadow: d && !otp.error ? `0 0 0 3px rgba(0,131,211,0.12)` : "none",
                background: isVerifying ? "#f9fafb" : "#fff",
                transition: "border-color 0.15s, box-shadow 0.15s",
              }}
            />
          ))}
        </div>

        {otp.error && (
          <p style={{ fontSize: 12, color: "#ef4444", textAlign: "center", margin: "0 0 12px" }}>
            {otp.error}
          </p>
        )}

        <button
          type="submit"
          disabled={!isFilled || isVerifying}
          style={{
            width: "100%", padding: "12px 0",
            background: !isFilled || isVerifying ? "#6b9ab8" : BRAND.primary,
            color: "#fff", border: "none", borderRadius: 10,
            fontWeight: 700, fontSize: 15,
            cursor: !isFilled || isVerifying ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            marginBottom: 14,
          }}
        >
          {isVerifying ? (
            <>
              <i className="material-symbols-outlined" style={{ fontSize: 18, animation: "otpSpin 1s linear infinite" }}>progress_activity</i>
              Verifying…
            </>
          ) : (
            <>
              <i className="material-symbols-outlined" style={{ fontSize: 18 }}>verified</i>
              Verify & Continue
            </>
          )}
        </button>
      </form>

      {/* Resend + back */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            style={{ background: "none", border: "none", fontSize: 13, color: "#6b7280", cursor: "pointer", padding: 0 }}
          >
            ← Change email
          </button>
        )}
        <button
          type="button"
          onClick={() => otp.resend(email, name)}
          disabled={otp.resendTimer > 0 || isVerifying}
          style={{
            background: "none", border: "none",
            fontSize: 13, cursor: otp.resendTimer > 0 ? "default" : "pointer",
            color: otp.resendTimer > 0 ? "#9ca3af" : BRAND.blue,
            padding: 0,
            marginLeft: onBack ? 0 : "auto",
          }}
        >
          {otp.resendTimer > 0 ? `Resend in ${otp.resendTimer}s` : "Resend code"}
        </button>
      </div>

      <p style={{ fontSize: 11, color: "#9ca3af", textAlign: "center", margin: "12px 0 0", lineHeight: 1.5 }}>
        Check your spam folder if you don't see it. Code expires in 10 min.
      </p>

      <style>{`@keyframes otpSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
